import { formatDateTime } from '@iris/i18n';
import { useLocale } from '@iris/i18n/react';
import { unwrap } from '@iris/shared';
import { useAuth } from '@iris/ui/auth/auth-provider';
import { cn } from '@iris/ui/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Ban, CircleAlert, Snowflake, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useShops } from '../shop-context';

/** The selected shop's subscription, for owner and manager (US-O20). */
export function useSubscription() {
  const { api } = useAuth();
  const { current } = useShops();
  return useQuery({
    queryKey: ['subscription', current?.merchantId],
    queryFn: async () => unwrap(await api.GET('/api/v1/merchant/subscription')),
    enabled: Boolean(current && current.role !== 'CASHIER' && current.status === 'ACTIVE'),
    staleTime: 300_000,
  });
}

export function useDay() {
  const { locale } = useLocale();
  return (iso: string) =>
    formatDateTime(Date.parse(iso.length === 10 ? `${iso}T12:00:00Z` : iso), locale, { dateStyle: 'long' });
}

/** ADR 0007 D2: a shop suspended by Ops, on every staff screen (the cashier too: earns are refused, vouchers not). */
export function SuspendedBanner() {
  const { t } = useTranslation();
  return (
    <p
      role="status"
      className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm font-medium text-destructive print:hidden"
    >
      <Ban aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{t('shops.suspendedBanner')}</span>
    </p>
  );
}

/**
 * ADR 0006 (owner decision M26: in the app, no push yet): the end coming within 7 days, the shop paused, or closing —
 * on every owner and manager screen, with the way to the billing page.
 */
export function SubscriptionBanner() {
  const { t } = useTranslation();
  const subscription = useSubscription();
  const day = useDay();
  const data = subscription.data;
  if (!data) return null;
  const end = data.endsOn ? day(data.endsOn) : '';
  // ADR 0006: warnings 7, 3 and 1 day before the end; paused the day after, no grace period
  const banner = data.closing
    ? { icon: Store, text: t('billing.banner.closing'), tone: 'muted' }
    : data.status === 'FROZEN'
      ? { icon: Snowflake, text: t('billing.banner.paused', { date: end }), tone: 'danger' }
      : data.endsOn && data.daysLeft <= 7
        ? {
            icon: CircleAlert,
            text: t('billing.banner.endingSoon', { count: data.daysLeft, date: end }),
            tone: data.daysLeft <= 1 ? 'danger' : 'warning',
          }
        : null;
  if (!banner) return null;
  const Icon = banner.icon;
  return (
    <Link
      to="/billing"
      role="status"
      className={cn(
        'mb-4 flex items-start gap-2 rounded-lg border p-3 text-sm font-medium print:hidden',
        banner.tone === 'danger' && 'border-destructive/40 bg-destructive/5 text-destructive',
        banner.tone === 'warning' && 'border-warning-600/40 bg-warning-600/5',
        banner.tone === 'muted' && 'bg-muted',
      )}
    >
      <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{banner.text}</span>
    </Link>
  );
}
